import type { FeedItem } from './types.js';

/**
 * Share an article using the best available mechanism:
 * 1. Web Share API (native dialog)
 * 2. Clipboard API (navigator.clipboard.writeText)
 * 3. Legacy execCommand('copy') fallback
 *
 * All errors are handled internally — nothing surfaces to the end user.
 */
export async function shareArticle(item: FeedItem, shadowRoot: ShadowRoot): Promise<void> {
  try {
    if (navigator.share) {
      await navigator.share({ title: item.title, url: item.link });
      return;
    }
  } catch (err) {
    // AbortError = user cancelled native share dialog — silently ignore
    if (err instanceof DOMException && err.name === 'AbortError') return;
    // Fall through to clipboard fallback
  }

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(item.link);
      showToast(shadowRoot, 'Link copied to clipboard');
      return;
    }
  } catch {
    // Fall through to execCommand fallback
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = item.link;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    showToast(shadowRoot, 'Link copied to clipboard');
  } catch {
    // Silently fail — never surface errors to end user
  }
}

/**
 * Display a temporary toast notification inside the Shadow DOM.
 * Auto-hides after 2 seconds with a 300ms fade-out transition.
 */
export function showToast(shadowRoot: ShadowRoot, message: string): void {
  const existing = shadowRoot.querySelector('.rss-toast');
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.className = 'rss-toast';
  toast.textContent = message;
  shadowRoot.appendChild(toast);

  requestAnimationFrame(() => {
    toast.classList.add('rss-toast-visible');
  });

  setTimeout(() => {
    toast.classList.remove('rss-toast-visible');
    setTimeout(() => toast.remove(), 300);
  }, 2000);
}
